import { useState } from 'react';
import {
  useDecideMtoQualityDocMutation,
  useDeleteMtoQualityDocMutation,
  useGetMtoLotQuery,
  useLazyDownloadMtoQualityDocQuery,
} from '@shared/api';
import type { QualityDoc } from '@entities/mto';
import {
  apiErrorMessage,
  docTypeLabel,
  fmtDate,
  qualityDocStatusLabel,
  qualityDocStatusTone,
} from '@entities/mto';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';
import { IconDownload, IconTrash } from '@shared/ui/icons';
import { saveBlob } from '../helpers';
import {
  DocsWrap,
  DownloadButton,
  Empty,
  ErrBox,
  IconButton,
  Muted,
  RowActions,
  Table,
  TableScroll,
} from '../styles';
import { AddDocForm } from './AddDocForm';

export function LotDocsRow({
  doc,
  lotId,
  canWrite,
  onError,
}: {
  doc: QualityDoc;
  lotId: string;
  canWrite: boolean;
  onError: (msg: string) => void;
}) {
  const [decide, { isLoading: deciding }] = useDecideMtoQualityDocMutation();
  const [del, { isLoading: deleting }] = useDeleteMtoQualityDocMutation();
  const [download, { isFetching: downloading }] = useLazyDownloadMtoQualityDocQuery();

  async function handleDecide(decision: 'confirmed' | 'draft') {
    onError('');
    try {
      await decide({ id: doc.id, lotId, decision }).unwrap();
    } catch (err) {
      onError(apiErrorMessage(err) ?? 'Не удалось изменить статус документа.');
      // eslint-disable-next-line no-console
      console.error('decideMtoQualityDoc failed', err);
    }
  }

  async function handleDelete() {
    if (!window.confirm('Удалить документ качества? Действие нельзя отменить.')) return;
    onError('');
    try {
      await del({ id: doc.id, lotId }).unwrap();
    } catch (err) {
      onError(apiErrorMessage(err) ?? 'Не удалось удалить документ качества.');
      // eslint-disable-next-line no-console
      console.error('deleteMtoQualityDoc failed', err);
    }
  }

  async function handleDownload() {
    onError('');
    try {
      const blob = await download(doc.id).unwrap();
      saveBlob(blob, doc.file_name ?? `quality_doc_${doc.id}`);
    } catch (err) {
      onError(apiErrorMessage(err) ?? 'Не удалось скачать файл документа.');
      // eslint-disable-next-line no-console
      console.error('downloadMtoQualityDoc failed', err);
    }
  }

  return (
    <tr>
      <td>{docTypeLabel(doc.doc_type)}</td>
      <td>{doc.number ?? '—'}</td>
      <td>{fmtDate(doc.doc_date)}</td>
      <td>{doc.issuer ?? '—'}</td>
      <td>{doc.valid_until ? fmtDate(doc.valid_until) : 'бессрочно'}</td>
      <td>
        <Badge $tone={qualityDocStatusTone(doc.status)}>{qualityDocStatusLabel(doc.status)}</Badge>
      </td>
      <td>
        {doc.has_file ? (
          <DownloadButton type="button" onClick={handleDownload} disabled={downloading}>
            <IconDownload size={14} />
            {downloading ? 'Готовлю…' : 'Файл'}
          </DownloadButton>
        ) : (
          <Muted as="span">нет файла</Muted>
        )}
      </td>
      <td>
        <RowActions>
          {doc.status === 'draft' ? (
            <Button
              type="button"
              $variant="secondary"
              $size="sm"
              disabled={!canWrite || deciding}
              title={!canWrite ? 'Только просмотр' : undefined}
              onClick={() => handleDecide('confirmed')}
            >
              Подтвердить
            </Button>
          ) : (
            <Button
              type="button"
              $variant="ghost"
              $size="sm"
              disabled={!canWrite || deciding}
              title={!canWrite ? 'Только просмотр' : undefined}
              onClick={() => handleDecide('draft')}
            >
              В черновик
            </Button>
          )}
          <IconButton
            type="button"
            onClick={handleDelete}
            disabled={!canWrite || deleting}
            aria-label="Удалить документ"
            title={!canWrite ? 'Только просмотр' : 'Удалить документ'}
          >
            <IconTrash size={15} />
          </IconButton>
        </RowActions>
      </td>
    </tr>
  );
}

export function LotDocs({ lotId, canWrite }: { lotId: string; canWrite: boolean }) {
  const { data, isLoading } = useGetMtoLotQuery(lotId);
  const [error, setError] = useState<string | null>(null);
  const docs = data?.quality_docs ?? [];

  return (
    <DocsWrap>
      {isLoading ? (
        <Muted>Загрузка документов…</Muted>
      ) : docs.length === 0 ? (
        <Empty>Документов качества по партии пока нет. Добавьте паспорт, сертификат или декларацию.</Empty>
      ) : (
        <TableScroll>
          <Table>
            <thead>
              <tr>
                <th scope="col">Тип</th>
                <th scope="col">Номер</th>
                <th scope="col">Дата</th>
                <th scope="col">Орган</th>
                <th scope="col">Действует до</th>
                <th scope="col">Статус</th>
                <th scope="col">Файл</th>
                <th scope="col" aria-label="Действия" />
              </tr>
            </thead>
            <tbody>
              {docs.map((d) => (
                <LotDocsRow
                  key={d.id}
                  doc={d}
                  lotId={lotId}
                  canWrite={canWrite}
                  onError={(m) => setError(m || null)}
                />
              ))}
            </tbody>
          </Table>
        </TableScroll>
      )}
      {error && <ErrBox>{error}</ErrBox>}
      {canWrite && <AddDocForm lotId={lotId} canWrite={canWrite} onError={(m) => setError(m || null)} />}
    </DocsWrap>
  );
}
